"use client";

import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import useModalStore from "@/hooks/useModalStore";
import useUserStore from "@/stores/useUserStore";
import ModalElement from "@/components/Modal/Modal";
import Button from "@/components/Elements/Button";
import { resendVerifyEmail } from "@/actions/user";
import Link from "next/link";

const REMINDER_INTERVAL_MS = 24 * 60 * 60 * 1000;

// Two uses: login refused with ERROR_EMAIL_NOT_VERIFIED (new account, opened from the login forms), or a
// reminder for a logged-in user whose email is still unverified. The reminder opens itself at most once per day.
// The identifier sent to the API is the customer number: several accounts may share one email address.
const VerifyEmailModal: React.FC = () => {
  const { verifyEmailModal } = useModalStore();
  const { userInfo } = useUserStore();
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (userInfo?.userIsEmailVerified !== "no" || !userInfo?.userCustomerNo) return;

    const key = `verifyEmailReminder:${userInfo.userId}`;

    try {
      if (Date.now() - Number(localStorage.getItem(key) || 0) < REMINDER_INTERVAL_MS) return;
      localStorage.setItem(key, String(Date.now()));
    } catch {
      return;
    }

    verifyEmailModal.onOpen(String(userInfo.userCustomerNo), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.userIsEmailVerified, userInfo?.userCustomerNo, userInfo?.userId]);

  const resend = async () => {
    if (!verifyEmailModal.identifier) return;

    setIsSending(true);

    try {
      await resendVerifyEmail(verifyEmailModal.identifier);
      toast.success("Ett verifieringsmail är skickat.");
      verifyEmailModal.onClose();
    } catch {
      toast.error("Kunde inte skicka mailet, försök igen senare.");
    } finally {
      setIsSending(false);
    }
  };

  const isReminder = verifyEmailModal.reminder;

  return (
    <ModalElement
      isOpen={verifyEmailModal.isOpen}
      onClose={verifyEmailModal.onClose}
      modalTitle="Verifiera din e-postadress"
      size="lg"
      modalBody={
        <div className="flex flex-col space-y-6">
          {isReminder ? (
            <>
              <p className="text-base">
                Vi behöver bekräfta att e-postadressen på ditt konto{" "}
                <strong>{userInfo?.username}</strong> stämmer. Klicka på knappen så skickar vi
                en verifieringslänk till:
              </p>
              <p className="text-base">
                <strong>{userInfo?.userEmail}</strong>{" "}
                <Link
                  href="/mina-sidor/mitt-konto"
                  className="text-blue underline ml-2"
                  onClick={verifyEmailModal.onClose}
                >
                  Byt e-postadress
                </Link>
              </p>
              <p className="text-base">Kolla även skräpposten.</p>
            </>
          ) : (
            <p className="text-base">
              Du behöver verifiera din e-postadress innan du kan logga in. Vi har skickat ett
              mail med en verifieringslänk till den adress du angav vid registreringen. Kolla
              även skräpposten.
            </p>
          )}
          <div className="flex flex-row space-x-3">
            <Button
              bgColor="transparent"
              textColor="blue"
              borderColor="blue"
              onClick={verifyEmailModal.onClose}
            >
              {isReminder ? "Påminn mig senare" : "Stäng"}
            </Button>
            <Button
              bgColor="blue"
              onClick={resend}
              disabled={isSending}
              isLoading={isSending}
            >
              {isReminder ? "Skicka verifieringslänk" : "Skicka ny verifieringslänk"}
            </Button>
          </div>
        </div>
      }
    />
  );
};

export default VerifyEmailModal;
